import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { mlmAPI } from '../../services/api';
import { Users, GitBranch, Trophy, Wallet, Activity, DollarSign, ShieldCheck, Coins, Globe, TrendingUp } from 'lucide-react';

const MLMDashboard = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [networkStats, setNetworkStats] = useState(null);
  const [activeStatus, setActiveStatus] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      mlmAPI.getNetworkStats(),
      mlmAPI.getActiveStatus(),
      mlmAPI.getWallet(),
    ])
      .then(([statsRes, activeRes, walletRes]) => {
        setNetworkStats(statsRes.data);
        setActiveStatus(activeRes.data);
        setWallet(walletRes.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
      </div>
    );
  }

  const stats = [
    { label: 'Total Centers', value: networkStats?.total_centers ?? '—', icon: Users, color: 'from-blue-500 to-cyan-500' },
    { label: 'Total Team', value: networkStats?.total_team ?? '—', icon: Users, color: 'from-green-500 to-emerald-500' },
    { label: 'Weak Leg VU', value: networkStats?.weak_leg_vu ?? '—', icon: Activity, color: 'from-purple-500 to-pink-500' },
    { label: 'Current Rank', value: networkStats?.current_rank || '—', icon: Trophy, color: 'from-orange-500 to-red-500' },
  ];

  const quickLinks = [
    { label: 'Binary Tree', href: '/mlm/binary-tree', icon: GitBranch, desc: 'View binary network' },
    { label: 'Unilevel Tree', href: '/mlm/unilevel-tree', icon: TrendingUp, desc: 'View downline' },
    { label: 'Rank Progression', href: '/mlm/rank-progression', icon: Trophy, desc: 'Check rank progress' },
    { label: 'Wallet', href: '/mlm/wallet', icon: Wallet, desc: 'Commissions & balance' },
    { label: 'Sttora Points', href: '/mlm/sttora-points', icon: Coins, desc: 'Points & rewards' },
    { label: 'Global Pool', href: '/mlm/global-pool', icon: Globe, desc: 'Pool qualifications' },
  ];

  const cardClass = `${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl p-6 shadow-sm border ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <div>
        <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>MLM Dashboard</h1>
        <p className={`mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Your network overview and quick actions</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s, i) => (
          <motion.div key={s.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className={cardClass}>
            <div className={`inline-flex p-3 rounded-2xl bg-gradient-to-r ${s.color} text-white mb-3`}>
              <s.icon className="w-6 h-6" />
            </div>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{s.label}</p>
            <p className={`text-2xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{s.value}</p>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {activeStatus && (
          <div className={cardClass}>
            <div className="flex items-center justify-between mb-3">
              <h3 className={`font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>Active Status</h3>
              <ShieldCheck className={`w-5 h-5 ${activeStatus.is_active ? 'text-green-500' : 'text-red-500'}`} />
            </div>
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${activeStatus.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              {activeStatus.is_active ? 'Active' : 'Inactive'}
            </span>
            {activeStatus.current_cycle && (
              <p className={`mt-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Current Cycle: <span className="font-medium">{activeStatus.current_cycle?.cycle_number || activeStatus.current_cycle}</span>
              </p>
            )}
          </div>
        )}

        {wallet && (
          <div className={cardClass}>
            <div className="flex items-center justify-between mb-3">
              <h3 className={`font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>Commission Wallet</h3>
              <DollarSign className="w-5 h-5 text-green-500" />
            </div>
            <p className={`text-3xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>${wallet.balance?.toFixed(2) || '0.00'}</p>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Available Balance</p>
          </div>
        )}

        {activeStatus?.current_cycle && (
          <div className={cardClass}>
            <div className="flex items-center justify-between mb-3">
              <h3 className={`font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>Current Cycle</h3>
              <Activity className="w-5 h-5 text-blue-500" />
            </div>
            <p className={`text-lg font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>C{activeStatus.current_cycle.cycle_number}</p>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Left VU: {networkStats?.total_left_vu || 0} | Right VU: {networkStats?.total_right_vu || 0}
            </p>
          </div>
        )}
      </div>

      <div>
        <h2 className={`text-xl font-bold mb-4 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>Quick Links</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {quickLinks.map((link, i) => (
            <motion.div
              key={link.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              onClick={() => navigate(link.href)}
              className={`${cardClass} hover:shadow-md transition-shadow cursor-pointer`}
            >
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-2xl ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <link.icon className={`w-6 h-6 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`} />
                </div>
                <div>
                  <h3 className={`font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{link.label}</h3>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{link.desc}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

export default MLMDashboard;
